/**
 * P-040 — the human wording and the category thumbnails of the board permissions dialog.
 *
 * The dialog used to print the service's own action keys (`item.create`, `cell.update`, …) in a
 * bare list, next to eight empty cards. monday prints a sentence per capability under an
 * illustrated card, captured verbatim for *Items* in `[cap:board-permission-levels]`:
 *
 * > Create items · View items · Edit items · Move items within a group · Move items across
 * > groups · Move items to other boards · Archive self-created items · Archive items created by
 * > other users · Delete self-created items · Delete items created by other users ·
 * > Change new items terminology
 *
 * Our `can()` model is coarser than monday's per-capability one (P1 §5.3 renders all eight
 * categories **derived** from the chosen level), so the map below is monday's wording applied to
 * the actions we actually have — `item.move` covers all three of monday's "move" rows, and
 * `item.archive` / `item.delete` do not split self-created from others'. Never an action key.
 *
 * The thumbnails are ours by design: monday's card artwork may not ship (`[09§B.1 licence]`,
 * open question 104), so each card carries the same schematic of the surface it governs in
 * token colours, at the captured 150 × 155 / 158 px pitch.
 */

/** Every board action the permissions service can return, in monday's voice. */
const ACTION_LABELS: Record<string, string> = {
  /* Items */
  'item.create': 'Create items',
  'item.read': 'View items',
  'item.update': 'Edit items',
  'item.move': 'Move items within a group, across groups and to other boards',
  'item.duplicate': 'Duplicate items',
  'item.archive': 'Archive items',
  'item.delete': 'Delete items',
  'item.assign': 'Assign people to items',
  'cell.read': 'View column values',
  'cell.update': 'Edit column values',

  /* General */
  'board.read': 'View this board',
  'board.update': 'Rename the board and edit its description',
  'board.settings.update': 'Change board settings',
  'board.permissions.update': 'Change board permissions',
  'board.subscribers.manage': 'Add and remove board members',
  'board.owners.manage': 'Manage board owners',
  'board.duplicate': 'Duplicate the board',
  'board.export': 'Export the board',
  'board.archive': 'Archive the board',
  'board.delete': 'Delete the board',
  'board.activity.read': 'See the activity log',

  /* Updates */
  'update.create': 'Write updates',
  'update.edit_own': 'Edit self-written updates',
  'update.edit_any': 'Edit updates written by other users',
  'update.delete_own': 'Delete self-written updates',
  'update.delete_any': 'Delete updates written by other users',
  'update.like': 'Like updates',
  'update.pin': 'Pin an update to the top',

  /* Columns */
  'column.create': 'Create columns',
  'column.update': 'Edit columns',
  'column.reorder': 'Reorder columns',
  'column.delete': 'Delete columns',
  'column.restrict': 'Restrict who can see or edit a column',

  /* Groups */
  'group.create': 'Create groups',
  'group.update': 'Rename groups and change their colour',
  'group.reorder': 'Reorder groups',
  'group.archive': 'Archive groups',
  'group.delete': 'Delete groups',

  /* Views */
  'view.create': 'Create views',
  'view.update': 'Edit views',
  'view.delete': 'Delete views',
  'view.reorder': 'Reorder views',
  'view.set_default': 'Set the default view',
  'view.publish': 'Share a view outside the board',
};

/**
 * Two categories re-word the same actions, exactly as monday's *Subitems* card repeats the
 * *Items* rows in the subitem's noun and its *Forms* card talks about forms, not views.
 */
const CATEGORY_OVERRIDES: Record<string, Record<string, string>> = {
  Subitems: {
    'item.create': 'Create subitems',
    'item.read': 'View subitems',
    'item.update': 'Edit subitems',
    'item.move': 'Move subitems between their parents',
    'item.duplicate': 'Duplicate subitems',
    'item.archive': 'Archive subitems',
    'item.delete': 'Delete subitems',
    'item.assign': 'Assign people to subitems',
    'cell.read': 'View subitem column values',
    'cell.update': 'Edit subitem column values',
  },
  Forms: {
    'view.create': 'Create forms',
    'view.update': 'Edit forms',
    'view.delete': 'Delete forms',
    'view.reorder': 'Reorder forms',
    'view.set_default': 'Set the default form',
    'view.publish': 'Publish a form and share its link',
  },
};

/**
 * A capability row's sentence. An action the map has not met yet degrades to a readable
 * sentence rather than to its key, so a new action can never put `board.foo.bar` on screen.
 */
export function permissionLabel(action: string, category?: string): string {
  const override = category ? CATEGORY_OVERRIDES[category]?.[action] : undefined;
  if (override !== undefined) return override;
  const known = ACTION_LABELS[action];
  if (known !== undefined) return known;
  const words = action.replace(/[._]/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/* ───────────────────────────────────────────── the eight card thumbnails */

type Thumb = (props: { title: string }) => React.ReactElement;

const ROW = 'var(--ui-border-color, #c3c6d4)';
const SURFACE = 'var(--primary-background-color, #ffffff)';

/** A mini board: a title bar, four rows and a coloured status strip. */
function BoardThumb({ title, stripe }: { title: string; stripe: string[] }) {
  return (
    <svg viewBox="0 0 138 109" width="138" height="109" role="img" aria-label={title} focusable="false">
      <rect x="0" y="0" width="138" height="109" rx="8" fill="var(--allgrey-background-color, #eceff8)" />
      <rect x="19" y="17" width="100" height="75" rx="4" fill={SURFACE} />
      <rect x="27" y="25" width="42" height="4" rx="2" fill={ROW} />
      {[0, 1, 2, 3].map((index) => (
        <rect key={index} x="27" y={37 + index * 12} width="52" height="4" rx="2" fill={ROW} opacity="0.7" />
      ))}
      {stripe.map((colour, index) => (
        <rect key={colour} x="87" y={35 + index * 12} width="24" height="8" rx="2" fill={colour} />
      ))}
    </svg>
  );
}

const STATUS = [
  'var(--positive-color, #00c875)',
  'var(--warning-color, #fdab3d)',
  'var(--negative-color, #e2445c)',
  'var(--primary-color, #0073ea)',
];

const THUMBS: Record<string, Thumb> = {
  Items: ({ title }) => <BoardThumb title={title} stripe={STATUS} />,
  Subitems: ({ title }) => (
    <svg viewBox="0 0 138 109" width="138" height="109" role="img" aria-label={title} focusable="false">
      <rect x="0" y="0" width="138" height="109" rx="8" fill="var(--allgrey-background-color, #eceff8)" />
      <rect x="19" y="17" width="100" height="75" rx="4" fill={SURFACE} />
      <rect x="27" y="27" width="58" height="5" rx="2.5" fill={ROW} />
      {[0, 1, 2].map((index) => (
        <g key={index}>
          <rect x="39" y={42 + index * 13} width="46" height="4" rx="2" fill={ROW} opacity="0.7" />
          <rect x="93" y={41 + index * 13} width="18" height="6" rx="2" fill={STATUS[index]} />
        </g>
      ))}
    </svg>
  ),
  General: ({ title }) => <BoardThumb title={title} stripe={STATUS} />,
  Updates: ({ title }) => (
    <svg viewBox="0 0 138 109" width="138" height="109" role="img" aria-label={title} focusable="false">
      <rect x="0" y="0" width="138" height="109" rx="8" fill="var(--allgrey-background-color, #eceff8)" />
      <rect x="19" y="17" width="100" height="75" rx="4" fill={SURFACE} />
      <rect x="19" y="17" width="46" height="75" rx="4" fill="var(--allgrey-background-color, #eceff8)" opacity="0.6" />
      {[0, 1].map((index) => (
        <g key={index}>
          <circle cx="78" cy={40 + index * 24} r="6" fill="var(--primary-color, #0073ea)" opacity="0.85" />
          <rect x="89" y={37 + index * 24} width="24" height="4" rx="2" fill={ROW} />
          <rect x="89" y={45 + index * 24} width="16" height="4" rx="2" fill={ROW} opacity="0.7" />
        </g>
      ))}
    </svg>
  ),
  Columns: ({ title }) => (
    <svg viewBox="0 0 138 109" width="138" height="109" role="img" aria-label={title} focusable="false">
      <rect x="0" y="0" width="138" height="109" rx="8" fill="var(--allgrey-background-color, #eceff8)" />
      {[0, 1, 2].map((column) => (
        <rect key={column} x={26 + column * 30} y="24" width="24" height="61" rx="4" fill={SURFACE} />
      ))}
      {STATUS.map((colour, index) => (
        <rect key={colour} x="30" y={30 + index * 14} width="16" height="10" rx="2" fill={colour} />
      ))}
      {[0, 1, 2, 3].map((index) => (
        <rect key={index} x="60" y={30 + index * 14} width="16" height="10" rx="2" fill={ROW} opacity="0.6" />
      ))}
      {[0, 1, 2, 3].map((index) => (
        <rect key={index} x="90" y={30 + index * 14} width="16" height="10" rx="2" fill="var(--primary-color, #0073ea)" opacity="0.5" />
      ))}
    </svg>
  ),
  Groups: ({ title }) => (
    <svg viewBox="0 0 138 109" width="138" height="109" role="img" aria-label={title} focusable="false">
      <rect x="0" y="0" width="138" height="109" rx="8" fill="var(--allgrey-background-color, #eceff8)" />
      <rect x="19" y="17" width="100" height="75" rx="4" fill={SURFACE} />
      {[0, 1].map((group) => (
        <g key={group}>
          <rect x="27" y={26 + group * 32} width="30" height="5" rx="2.5" fill={STATUS[group]} />
          {[0, 1].map((index) => (
            <rect key={index} x="27" y={37 + group * 32 + index * 9} width="58" height="4" rx="2" fill={ROW} opacity="0.7" />
          ))}
        </g>
      ))}
    </svg>
  ),
  Views: ({ title }) => (
    <svg viewBox="0 0 138 109" width="138" height="109" role="img" aria-label={title} focusable="false">
      <rect x="0" y="0" width="138" height="109" rx="8" fill="var(--allgrey-background-color, #eceff8)" />
      <rect x="19" y="17" width="100" height="75" rx="4" fill={SURFACE} />
      <rect x="27" y="25" width="26" height="8" rx="3" fill="var(--primary-color, #0073ea)" />
      <rect x="57" y="25" width="26" height="8" rx="3" fill={ROW} opacity="0.6" />
      <rect x="87" y="25" width="20" height="8" rx="3" fill={ROW} opacity="0.6" />
      {[0, 1, 2].map((index) => (
        <rect key={index} x="27" y={43 + index * 12} width="80" height="6" rx="3" fill={ROW} opacity="0.55" />
      ))}
    </svg>
  ),
  Forms: ({ title }) => (
    <svg viewBox="0 0 138 109" width="138" height="109" role="img" aria-label={title} focusable="false">
      <rect x="0" y="0" width="138" height="109" rx="8" fill="var(--allgrey-background-color, #eceff8)" />
      <rect x="31" y="15" width="76" height="79" rx="4" fill={SURFACE} />
      <rect x="39" y="25" width="34" height="5" rx="2.5" fill="var(--primary-color, #0073ea)" />
      {[0, 1, 2].map((index) => (
        <rect key={index} x="39" y={38 + index * 14} width="60" height="9" rx="3" fill={ROW} opacity="0.45" />
      ))}
      <rect x="39" y="80" width="28" height="9" rx="4" fill="var(--primary-color, #0073ea)" />
    </svg>
  ),
};

/** The 138 × 109 schematic inside a category card. */
export function CategoryThumbnail({ category }: { category: string }): React.ReactElement {
  const Thumb = THUMBS[category] ?? THUMBS.Items;
  return <Thumb title={`${category} permissions`} />;
}
